/**
 * HomePage — a "Home" real do painel Cobanq (antes DashboardMockupPage).
 *
 * Consome /dashboard/overview e (lazy) /dashboard/geo/cidades|bairros.
 * 9 secoes:
 *   1. Visao geral (13 KPIs) — cards com icone colorido semantico
 *   2. Meta de recebimento de hoje (progress + resumo)
 *   3. Recebimentos 30 dias (recharts LineChart com area)
 *   4. Precisa da sua atencao (4 alertas)
 *   5. Carteira em atraso (buckets + donut lateral)
 *   6. Oportunidades de receita
 *   7. Saude da operacao (RadialBarChart)
 *   8. Movimentacoes recentes (timeline)
 *   9. Distribuicao de clientes no mapa (drill-down UF > cidade > bairro)
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Line,
  Pie,
  PieChart,
  PolarAngleAxis,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useAuth } from "../../contexts/AuthContext";
import {
  BR_ESTADOS_PATHS,
  BR_VIEWBOX,
} from "../../assets/mapas/brEstadosPaths";
import {
  getDashboardOverview,
  getGeoCidades,
  getGeoBairros,
  type DashboardKpis,
  type DashboardOverview,
  type Alerta,
  type AlertaKey,
  type AtrasoBucket,
  type AtrasoBucketLabel,
  type Movimentacao,
  type MovimentacaoTipo,
  type OportunidadeItem,
  type OportunidadeKey,
  type SaudeLabel,
  type GeoNamedCount,
} from "../../services/dashboard.service";

/* ============================================================================
 * helpers
 * ==========================================================================*/

function formatBRL(v: number | null): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(v ?? 0);
}

function formatBRLCompact(v: number | null): string {
  const value = v ?? 0;
  if (Math.abs(value) >= 1_000_000) {
    return `R$ ${(value / 1_000_000).toFixed(1).replace(".", ",")}M`;
  }
  if (Math.abs(value) >= 1_000) {
    return `R$ ${(value / 1_000).toFixed(1).replace(".", ",")}k`;
  }
  return formatBRL(value);
}

// Percentuais derivados vem null quando nao ha base pra calcular (ex:
// adimplenciaPct sem parcelas cadastradas). Mostrar "—" evita mostrar
// 100% enganosamente quando so ha sistema vazio.
function formatPct(v: number | null): string {
  if (v === null || Number.isNaN(v)) return "—";
  return `${v.toFixed(1).replace(".", ",")}%`;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function relativeTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  const minutes = Math.max(0, Math.floor(diffMs / 60_000));
  if (minutes < 1) return "agora";
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours}h`;
  const days = Math.floor(hours / 24);
  return `há ${days}d`;
}

function cx(...cls: Array<string | false | null | undefined>): string {
  return cls.filter(Boolean).join(" ");
}

/* ============================================================================
 * accent palette per KPI
 * ==========================================================================*/

type AccentTone = "blue" | "green" | "orange" | "red" | "yellow" | "violet";

const ACCENT_STYLES: Record<
  AccentTone,
  { iconBg: string; iconFg: string; ring: string; text: string; hex: string }
> = {
  blue: {
    iconBg: "bg-blue-100 dark:bg-blue-900/40",
    iconFg: "text-blue-600 dark:text-blue-400",
    ring: "ring-blue-500/20",
    text: "text-blue-600 dark:text-blue-400",
    hex: "#2196F3",
  },
  green: {
    iconBg: "bg-emerald-100 dark:bg-emerald-900/40",
    iconFg: "text-emerald-600 dark:text-emerald-400",
    ring: "ring-emerald-500/20",
    text: "text-emerald-600 dark:text-emerald-400",
    hex: "#43A047",
  },
  orange: {
    iconBg: "bg-orange-100 dark:bg-orange-900/40",
    iconFg: "text-orange-600 dark:text-orange-400",
    ring: "ring-orange-500/20",
    text: "text-orange-600 dark:text-orange-400",
    hex: "#FC5B00",
  },
  red: {
    iconBg: "bg-red-100 dark:bg-red-900/40",
    iconFg: "text-red-600 dark:text-red-400",
    ring: "ring-red-500/20",
    text: "text-red-600 dark:text-red-400",
    hex: "#E53935",
  },
  yellow: {
    iconBg: "bg-amber-100 dark:bg-amber-900/40",
    iconFg: "text-amber-600 dark:text-amber-400",
    ring: "ring-amber-500/20",
    text: "text-amber-600 dark:text-amber-400",
    hex: "#FB8C00",
  },
  violet: {
    iconBg: "bg-violet-100 dark:bg-violet-900/40",
    iconFg: "text-violet-600 dark:text-violet-400",
    ring: "ring-violet-500/20",
    text: "text-violet-600 dark:text-violet-400",
    hex: "#8B5CF6",
  },
};

type KpiKey = keyof DashboardKpis;

const KPI_ACCENTS: Record<KpiKey, AccentTone> = {
  capitalNaRua: "blue",
  totalAReceber: "blue",
  recebidoNoMes: "green",
  venceHoje: "orange",
  emAtraso: "red",
  disponivel: "yellow",
  clientesAtivos: "violet",
  ticketMedio: "blue",
  inadimplenciaPct: "red",
  adimplenciaPct: "green",
  recuperacaoPct: "green",
  renovacaoPct: "orange",
  jurosAReceber: "blue",
};

/* ============================================================================
 * inline heroicons (outline) — kept minimal, no icon lib
 * ==========================================================================*/

type IconName =
  | "wallet"
  | "arrow-down-tray"
  | "check-circle"
  | "calendar"
  | "exclamation-triangle"
  | "credit-card"
  | "users"
  | "target"
  | "trending-down"
  | "trending-up"
  | "arrow-path"
  | "currency-dollar"
  | "clock"
  | "sparkles"
  | "handshake"
  | "plus-circle"
  | "arrow-right";

const ICON_PATHS: Record<IconName, string> = {
  wallet:
    "M21 12a2.25 2.25 0 0 0-2.25-2.25H15a3 3 0 1 1 0-6h3.75A2.25 2.25 0 0 1 21 6v6Zm0 0v6a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 18V6a2.25 2.25 0 0 1 2.25-2.25h9.75",
  "arrow-down-tray":
    "M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3",
  "check-circle":
    "M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z",
  calendar:
    "M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5",
  "exclamation-triangle":
    "M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z",
  "credit-card":
    "M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z",
  users:
    "M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z",
  target:
    "M15.042 21.672 13.684 16.6m0 0-2.51 2.225.569-9.47 5.227 7.917-3.286-.672ZM12 2.25V4.5m5.834.166-1.591 1.591M20.25 10.5H18M7.757 14.743l-1.59 1.59M6 10.5H3.75m4.007-4.243-1.59-1.59",
  "trending-down":
    "M2.25 6 9 12.75l4.286-4.286a11.948 11.948 0 0 1 4.306 6.43l.776 2.898m0 0 3.182-5.511m-3.182 5.51-5.511-3.181",
  "trending-up":
    "M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941",
  "arrow-path":
    "M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99",
  "currency-dollar":
    "M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-2.121 0-3.879-.879-3.879-2.818 0-1.94 1.758-2.818 3.879-2.818.768 0 1.536.219 2.121.659l.879.659M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z",
  clock:
    "M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z",
  sparkles:
    "M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z",
  handshake:
    "M9 8.25H7.5a2.25 2.25 0 0 0-2.25 2.25v9a2.25 2.25 0 0 0 2.25 2.25h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25H15M9 12l2.25 2.25L15 10.5M9 8.25V6a2.25 2.25 0 0 1 4.5 0v2.25M9 8.25h4.5",
  "plus-circle":
    "M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z",
  "arrow-right":
    "M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3",
};

function Icon({
  name,
  className,
}: {
  name: IconName;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d={ICON_PATHS[name]} />
    </svg>
  );
}

function CardIcon({
  name,
  tone,
}: {
  name: IconName;
  tone: AccentTone;
}) {
  const s = ACCENT_STYLES[tone];
  return (
    <div
      className={cx(
        "flex h-12 w-12 shrink-0 items-center justify-center rounded-xl",
        s.iconBg
      )}
    >
      <Icon name={name} className={cx("h-6 w-6", s.iconFg)} />
    </div>
  );
}

/* ============================================================================
 * page
 * ==========================================================================*/

export function HomePage() {
  const { user } = useAuth();
  const { t } = useTranslation();

  const [data, setData] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchOverview = useCallback(() => {
    setLoading(true);
    setError(null);
    getDashboardOverview()
      .then((d) => setData(d))
      .catch((err: unknown) => {
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const nome =
    user?.nome?.split(/\s+/)[0] ?? t("dashboard.default_name");

  return (
    <div className="min-h-screen bg-muted/30 dark:bg-gray-950">
      <div className="mx-auto max-w-7xl px-4 py-6 md:px-6 md:py-8">
        {/* Header */}
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-foreground md:text-3xl">
            {t("dashboard.welcome", { name: nome })}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("dashboard.summary")}
          </p>
        </header>

        {loading && !data && <DashboardSkeleton />}

        {!loading && error && !data && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center dark:border-red-900/40 dark:bg-red-950/30">
            <p className="mb-3 text-sm text-red-600 dark:text-red-400">
              {t("dashboard.load_error")}: {error.message}
            </p>
            <button
              onClick={fetchOverview}
              className="rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium hover:bg-muted"
            >
              {t("dashboard.retry")}
            </button>
          </div>
        )}

        {data && (
          <div className="space-y-6">
            <VisaoGeralSection data={data} t={t} />
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[2fr_1fr]">
              <RecebimentosChartSection data={data} t={t} />
              <MetaHojeSection data={data} t={t} />
            </div>
            <AtencaoSection data={data} t={t} />
            <AtrasoSection data={data} t={t} />
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.4fr_1fr]">
              <OportunidadesSection data={data} t={t} />
              <SaudeSection data={data} t={t} />
            </div>
            <MovimentacoesSection data={data} t={t} />
            <MapaClientesSection data={data} t={t} />
          </div>
        )}
      </div>
    </div>
  );
}

/* ============ Skeleton ============ */

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            className="h-28 animate-pulse rounded-2xl border border-border bg-card"
          />
        ))}
      </div>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-56 animate-pulse rounded-2xl border border-border bg-card"
        />
      ))}
    </div>
  );
}

/* ============ Shared card shell ============ */

function Card({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cx(
        "rounded-2xl border border-border bg-card shadow-sm transition-shadow hover:shadow-md",
        className
      )}
    >
      {children}
    </div>
  );
}

function SectionCard({
  title,
  description,
  action,
  children,
  className,
}: {
  title?: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Card className={cx("p-5 md:p-6", className)}>
      {(title || action) && (
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            {title && (
              <h2 className="text-base font-semibold text-foreground md:text-lg">
                {title}
              </h2>
            )}
            {description && (
              <p className="mt-1 text-xs text-muted-foreground md:text-sm">
                {description}
              </p>
            )}
          </div>
          {action}
        </div>
      )}
      {children}
    </Card>
  );
}

/* ============ 1. Visao Geral (KPIs) ============ */

interface KpiVM {
  key: KpiKey;
  label: string;
  value: string;
  icon: IconName;
  tone: AccentTone;
  deltaPct: number | null;
  hint?: string;
}

function VisaoGeralSection({
  data,
  t,
}: {
  data: DashboardOverview;
  t: TFunction;
}) {
  const k = data.kpis;

  const list: KpiVM[] = [
    {
      key: "capitalNaRua",
      label: t("dashboard.kpi_capital_na_rua"),
      value: formatBRL(k.capitalNaRua.value),
      icon: "wallet",
      tone: KPI_ACCENTS.capitalNaRua,
      deltaPct: k.capitalNaRua.deltaPct,
    },
    {
      key: "totalAReceber",
      label: t("dashboard.kpi_total_a_receber"),
      value: formatBRL(k.totalAReceber.value),
      icon: "arrow-down-tray",
      tone: KPI_ACCENTS.totalAReceber,
      deltaPct: k.totalAReceber.deltaPct,
    },
    {
      key: "recebidoNoMes",
      label: t("dashboard.kpi_recebido_no_mes"),
      value: formatBRL(k.recebidoNoMes.value),
      icon: "check-circle",
      tone: KPI_ACCENTS.recebidoNoMes,
      deltaPct: k.recebidoNoMes.deltaPct,
    },
    {
      key: "venceHoje",
      label: t("dashboard.kpi_vence_hoje"),
      value: formatBRL(k.venceHoje.value),
      icon: "calendar",
      tone: KPI_ACCENTS.venceHoje,
      deltaPct: k.venceHoje.deltaPct,
    },
    {
      key: "emAtraso",
      label: t("dashboard.kpi_em_atraso"),
      value: formatBRL(k.emAtraso.value),
      icon: "exclamation-triangle",
      tone: KPI_ACCENTS.emAtraso,
      deltaPct: k.emAtraso.deltaPct,
    },
    {
      key: "clientesAtivos",
      label: t("dashboard.kpi_clientes_ativos"),
      value: String(k.clientesAtivos.value),
      icon: "users",
      tone: KPI_ACCENTS.clientesAtivos,
      deltaPct: k.clientesAtivos.deltaPct,
      hint: t("dashboard.kpi_hint_clientes_ativos"),
    },
    {
      key: "ticketMedio",
      label: t("dashboard.kpi_ticket_medio"),
      value: formatBRL(k.ticketMedio.value),
      icon: "target",
      tone: KPI_ACCENTS.ticketMedio,
      deltaPct: k.ticketMedio.deltaPct,
      hint: t("dashboard.kpi_hint_ticket_medio"),
    },
    {
      key: "inadimplenciaPct",
      label: t("dashboard.kpi_inadimplencia"),
      value: formatPct(k.inadimplenciaPct.value),
      icon: "trending-down",
      tone: KPI_ACCENTS.inadimplenciaPct,
      deltaPct: k.inadimplenciaPct.deltaPct,
    },
    {
      key: "adimplenciaPct",
      label: t("dashboard.kpi_adimplencia"),
      value: formatPct(k.adimplenciaPct.value),
      icon: "trending-up",
      tone: KPI_ACCENTS.adimplenciaPct,
      deltaPct: k.adimplenciaPct.deltaPct,
    },
    {
      key: "recuperacaoPct",
      label: t("dashboard.kpi_recuperacao"),
      value: formatPct(k.recuperacaoPct.value),
      icon: "arrow-path",
      tone: KPI_ACCENTS.recuperacaoPct,
      deltaPct: k.recuperacaoPct.deltaPct,
    },
    {
      key: "renovacaoPct",
      label: t("dashboard.kpi_renovacao"),
      value: formatPct(k.renovacaoPct.value),
      icon: "arrow-path",
      tone: KPI_ACCENTS.renovacaoPct,
      deltaPct: k.renovacaoPct.deltaPct,
    },
    {
      key: "jurosAReceber",
      label: t("dashboard.kpi_juros_a_receber"),
      value: formatBRL(k.jurosAReceber.value),
      icon: "currency-dollar",
      tone: KPI_ACCENTS.jurosAReceber,
      deltaPct: k.jurosAReceber.deltaPct,
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {list.map((kpi) => (
        <KpiCard key={kpi.key} kpi={kpi} t={t} />
      ))}
    </div>
  );
}

function KpiCard({ kpi, t }: { kpi: KpiVM; t: TFunction }) {
  const delta = kpi.deltaPct;
  const hasDelta = delta !== null && Number.isFinite(delta);
  const positive = hasDelta && (delta as number) >= 0;

  return (
    <Card className="p-5">
      <div className="flex items-start gap-4">
        <CardIcon name={kpi.icon} tone={kpi.tone} />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {kpi.label}
          </p>
          <p className="mt-1 truncate text-2xl font-bold tabular-nums text-foreground md:text-[1.7rem] md:leading-tight">
            {kpi.value}
          </p>
          {(hasDelta || kpi.hint) && (
            <div className="mt-1 flex items-center gap-2 text-xs">
              {hasDelta && (
                <span
                  className={cx(
                    "inline-flex items-center gap-0.5 font-semibold tabular-nums",
                    positive
                      ? "text-emerald-600 dark:text-emerald-400"
                      : "text-red-600 dark:text-red-400"
                  )}
                >
                  <span aria-hidden>{positive ? "↑" : "↓"}</span>
                  {Math.abs(delta as number).toFixed(1).replace(".", ",")}%
                </span>
              )}
              <span className="truncate text-muted-foreground">
                {hasDelta
                  ? t("dashboard.kpi_delta_vs_prev_month")
                  : kpi.hint}
              </span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

/* ============ 2. Meta hoje ============ */

function MetaHojeSection({
  data,
  t,
}: {
  data: DashboardOverview;
  t: TFunction;
}) {
  const navigate = useNavigate();
  const { meta, recebido, faltaReceber } = data.metaDia;
  const pct = meta > 0 ? Math.min(100, Math.round((recebido / meta) * 100)) : 0;
  const empty = recebido === 0;

  return (
    <SectionCard
      title={t("dashboard.section_meta_title")}
      description={t("dashboard.section_meta_desc")}
    >
      <div className="flex flex-col gap-5">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t("dashboard.meta_received_so_far")}
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span
              className={cx(
                "text-3xl font-bold tabular-nums",
                empty ? "text-muted-foreground" : "text-emerald-600 dark:text-emerald-400"
              )}
            >
              {formatBRL(recebido)}
            </span>
            <span className="text-sm font-medium text-muted-foreground tabular-nums">
              / {formatBRL(meta)}
            </span>
          </div>
          {empty && (
            <p className="mt-1 text-xs text-muted-foreground">
              Nenhum pagamento ainda hoje
            </p>
          )}
        </div>

        {/* progress bar */}
        <div>
          <div className="mb-2 flex items-center justify-between text-xs">
            <span className="font-medium text-muted-foreground">Progresso</span>
            <span className="font-semibold tabular-nums text-foreground">
              {pct}%
            </span>
          </div>
          <div className="h-3 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 rounded-xl bg-muted/40 p-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t("dashboard.meta_missing")}
            </div>
            <div className="mt-1 text-lg font-bold tabular-nums text-red-600 dark:text-red-400">
              {formatBRL(faltaReceber)}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t("dashboard.meta_daily")}
            </div>
            <div className="mt-1 text-lg font-bold tabular-nums text-foreground">
              {formatBRL(meta)}
            </div>
          </div>
        </div>

        <button
          onClick={() => navigate("/dashboard/emprestimos?tab=vencendo_hoje")}
          className="w-full rounded-xl bg-primary-600 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-700"
        >
          {t("dashboard.meta_cta")}
        </button>
      </div>
    </SectionCard>
  );
}

/* ============ 3. Recebimentos chart (recharts) ============ */

function RecebimentosChartSection({
  data,
  t,
}: {
  data: DashboardOverview;
  t: TFunction;
}) {
  const chartData = useMemo(() => {
    return data.chart30d.map((p) => {
      const d = new Date(p.date + "T00:00:00");
      const label = isNaN(d.getTime())
        ? p.date
        : d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
      return {
        label,
        previsto: p.previsto,
        recebido: p.recebido,
      };
    });
  }, [data.chart30d]);

  const hasData = chartData.some((p) => p.previsto > 0 || p.recebido > 0);

  return (
    <SectionCard
      title={t("dashboard.section_chart_title")}
      description={t("dashboard.section_chart_desc")}
      action={
        hasData ? (
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <span className="h-2 w-2 rounded-full bg-blue-500" />
              {t("dashboard.chart_previsto")}
            </span>
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              {t("dashboard.chart_recebido")}
            </span>
          </div>
        ) : null
      }
    >
      {!hasData ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          {t("dashboard.chart_empty")}
        </div>
      ) : (
        <div className="h-[280px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
            >
              <defs>
                <linearGradient id="fillRecebido" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#43A047" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#43A047" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="fillPrevisto" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2196F3" stopOpacity={0.08} />
                  <stop offset="100%" stopColor="#2196F3" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                stroke="currentColor"
                strokeDasharray="3 3"
                className="text-border"
                vertical={false}
              />
              <XAxis
                dataKey="label"
                stroke="currentColor"
                className="text-muted-foreground"
                tick={{ fontSize: 11, fill: "currentColor" }}
                tickLine={false}
                axisLine={false}
                interval="preserveStartEnd"
                minTickGap={20}
              />
              <YAxis
                stroke="currentColor"
                className="text-muted-foreground"
                tick={{ fontSize: 11, fill: "currentColor" }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => formatBRLCompact(Number(v))}
                width={60}
              />
              <Tooltip
                content={<ChartTooltip />}
                cursor={{
                  stroke: "#94a3b8",
                  strokeWidth: 1,
                  strokeDasharray: "4 4",
                }}
              />
              <Area
                type="monotone"
                dataKey="previsto"
                stroke="#2196F3"
                strokeWidth={2}
                strokeDasharray="6 4"
                fill="url(#fillPrevisto)"
                dot={false}
                activeDot={{ r: 4, fill: "#2196F3" }}
              />
              <Area
                type="monotone"
                dataKey="recebido"
                stroke="#43A047"
                strokeWidth={2.5}
                fill="url(#fillRecebido)"
                dot={false}
                activeDot={{ r: 5, fill: "#43A047" }}
              />
              {/* Enforce Line ordering so it renders as area+line */}
              <Line type="monotone" dataKey="recebido" stroke="none" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </SectionCard>
  );
}

interface TooltipPayloadItem {
  name?: string;
  value?: number;
  dataKey?: string;
  color?: string;
}

function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 shadow-lg">
      <div className="mb-1 text-xs font-semibold text-foreground">{label}</div>
      {payload.map((entry, i) => (
        <div
          key={`${entry.dataKey}-${i}`}
          className="flex items-center gap-2 text-xs"
        >
          <span
            className="h-2 w-2 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="capitalize text-muted-foreground">
            {entry.dataKey}
          </span>
          <span className="ml-auto font-semibold tabular-nums text-foreground">
            {formatBRL(entry.value ?? 0)}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ============ 4. Atenção ============ */

interface AlertVM {
  key: AlertaKey;
  icon: IconName;
  tone: AccentTone;
  title: string;
  detail: string;
  cta: string;
}

function alertVM(a: Alerta, t: TFunction): AlertVM {
  switch (a.key) {
    case "vencidos_ontem":
      return {
        key: a.key,
        icon: "exclamation-triangle",
        tone: "red",
        title: t("dashboard.alert_vencidos_ontem_title", { count: a.count }),
        detail: t("dashboard.alert_value_at_risk", { value: formatBRL(a.valor) }),
        cta: t("dashboard.alert_vencidos_ontem_cta"),
      };
    case "tres_dias_sem_pagar":
      return {
        key: a.key,
        icon: "clock",
        tone: "red",
        title: t("dashboard.alert_tres_dias_title", { count: a.count }),
        detail: t("dashboard.alert_value_open", { value: formatBRL(a.valor) }),
        cta: t("dashboard.alert_tres_dias_cta"),
      };
    case "renovar_hoje":
      return {
        key: a.key,
        icon: "arrow-path",
        tone: "orange",
        title: t("dashboard.alert_renovar_hoje_title", { count: a.count }),
        detail: t("dashboard.alert_value_potential", {
          value: formatBRL(a.valor),
        }),
        cta: t("dashboard.alert_renovar_hoje_cta"),
      };
    case "recuperados_semana":
      return {
        key: a.key,
        icon: "sparkles",
        tone: "green",
        title: t("dashboard.alert_recuperados_title"),
        detail: t("dashboard.alert_recuperados_detail", {
          count: a.count,
          value: formatBRL(a.valor),
        }),
        cta: t("dashboard.alert_recuperados_cta"),
      };
  }
}

function AtencaoSection({
  data,
  t,
}: {
  data: DashboardOverview;
  t: TFunction;
}) {
  const alerts = data.alertas.map((a) => alertVM(a, t));
  return (
    <SectionCard
      title={t("dashboard.section_attention_title")}
      description={t("dashboard.section_attention_desc")}
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {alerts.map((a) => {
          const tone = ACCENT_STYLES[a.tone];
          return (
            <div
              key={a.key}
              className="group flex flex-col rounded-xl border border-border p-4 transition-shadow hover:shadow-md"
            >
              <div className="mb-3 flex items-start justify-between">
                <CardIcon name={a.icon} tone={a.tone} />
                <span
                  className={cx(
                    "opacity-0 transition-opacity group-hover:opacity-100",
                    tone.iconFg
                  )}
                >
                  <Icon name="arrow-right" className="h-4 w-4" />
                </span>
              </div>
              <div className={cx("font-semibold", tone.text)}>{a.title}</div>
              <p className="mt-1 flex-1 text-sm text-muted-foreground">
                {a.detail}
              </p>
              {/* TODO(review): CTAs ainda nao tem rotas — decidir destino */}
              <button
                className={cx(
                  "mt-4 w-full rounded-lg border py-2 text-sm font-medium transition",
                  "border-border bg-card hover:bg-muted"
                )}
              >
                {a.cta}
              </button>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

/* ============ 5. Atraso + Donut ============ */

const BUCKET_LABEL_KEY: Record<AtrasoBucketLabel, string> = {
  "1d": "dashboard.atraso_1d",
  "2-3d": "dashboard.atraso_2_3d",
  "4-7d": "dashboard.atraso_4_7d",
  "8-15d": "dashboard.atraso_8_15d",
  "+15d": "dashboard.atraso_15d_plus",
};

/** verde → amarelo → laranja → vermelho → vermelho escuro */
const BUCKET_COLOR: Record<AtrasoBucketLabel, { from: string; to: string; solid: string }> = {
  "1d": { from: "from-emerald-100", to: "to-emerald-500", solid: "#10b981" },
  "2-3d": { from: "from-amber-100", to: "to-amber-500", solid: "#f59e0b" },
  "4-7d": { from: "from-orange-100", to: "to-orange-500", solid: "#f97316" },
  "8-15d": { from: "from-red-100", to: "to-red-500", solid: "#ef4444" },
  "+15d": { from: "from-red-200", to: "to-red-700", solid: "#b91c1c" },
};

function AtrasoSection({
  data,
  t,
}: {
  data: DashboardOverview;
  t: TFunction;
}) {
  const buckets: AtrasoBucket[] = data.atrasoBuckets;
  const total = buckets.reduce((s, b) => s + b.valor, 0);
  const totalClientes = buckets.reduce((s, b) => s + b.clientes, 0);

  const donutData = useMemo(
    () =>
      buckets
        .filter((b) => b.clientes > 0)
        .map((b) => ({
          name: t(BUCKET_LABEL_KEY[b.label]),
          value: b.clientes,
          color: BUCKET_COLOR[b.label].solid,
        })),
    [buckets, t]
  );

  return (
    <SectionCard
      title={t("dashboard.section_atraso_title")}
      description={t("dashboard.section_atraso_desc")}
    >
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.5fr_1fr]">
        {/* Barras */}
        <div className="space-y-4">
          {buckets.map((b) => {
            const c = BUCKET_COLOR[b.label];
            return (
              <div key={b.label}>
                <div className="mb-1.5 flex items-center justify-between gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: c.solid }}
                    />
                    <span className="font-medium text-foreground">
                      {t(BUCKET_LABEL_KEY[b.label])}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      · {t("dashboard.atraso_clients", { count: b.clientes })}
                    </span>
                  </div>
                  <strong className="text-sm tabular-nums text-foreground">
                    {formatBRL(b.valor)}
                  </strong>
                </div>
                <div className="h-2.5 overflow-hidden rounded-full bg-muted">
                  <div
                    className={cx(
                      "h-full rounded-full bg-gradient-to-r",
                      c.from,
                      c.to
                    )}
                    style={{ width: `${Math.max(2, b.pctMaxBucket)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Donut */}
        <div className="flex flex-col items-center justify-center">
          {donutData.length === 0 ? (
            <div className="flex h-[220px] items-center justify-center rounded-xl border border-dashed border-border px-6 text-center text-sm text-muted-foreground">
              {t("dashboard.atraso_total_summary", { count: 0 })}
            </div>
          ) : (
            <div className="relative h-[220px] w-full">
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={donutData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    stroke="none"
                  >
                    {donutData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<DonutTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              {/*
               * Texto sobre o buraco do donut fica LEGIVEL: quando a arte do
               * chart tem fatias vermelhas/laranjas atras, texto branco some.
               * Solucao: chip solido (bg-card/bg-white) preenchendo o miolo,
               * com o texto em cima. Circle diameter = innerRadius*2 - 8px de
               * folga pra nao encostar na borda das fatias.
               */}
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="flex h-[112px] w-[112px] flex-col items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("dashboard.atraso_total_label")}
                  </div>
                  <div className="mt-0.5 text-lg font-bold tabular-nums leading-tight text-red-600 dark:text-red-400">
                    {formatBRLCompact(total)}
                  </div>
                  <div className="mt-0.5 px-2 text-center text-[10px] leading-tight text-muted-foreground">
                    {t("dashboard.atraso_total_summary", { count: totalClientes })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </SectionCard>
  );
}

function DonutTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{
    name?: string;
    value?: number;
    payload?: { color?: string };
  }>;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0];
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 text-xs shadow-lg">
      <div className="flex items-center gap-2">
        <span
          className="h-2 w-2 rounded-full"
          style={{ backgroundColor: p.payload?.color }}
        />
        <span className="font-semibold text-foreground">{p.name}</span>
      </div>
      <div className="mt-1 tabular-nums text-muted-foreground">
        {p.value} clientes
      </div>
    </div>
  );
}

/* ============ 6. Oportunidades ============ */

const OPP_LABEL_KEY: Record<OportunidadeKey, string> = {
  renovacao: "dashboard.opp_renovacao",
  aumento_limite: "dashboard.opp_aumento_limite",
  inativos_recuperaveis: "dashboard.opp_inativos_recuperaveis",
};

const OPP_ICON: Record<OportunidadeKey, IconName> = {
  renovacao: "arrow-path",
  aumento_limite: "trending-up",
  inativos_recuperaveis: "users",
};

const OPP_TONE: Record<OportunidadeKey, AccentTone> = {
  renovacao: "green",
  aumento_limite: "orange",
  inativos_recuperaveis: "blue",
};

function OportunidadesSection({
  data,
  t,
}: {
  data: DashboardOverview;
  t: TFunction;
}) {
  const items: OportunidadeItem[] = data.oportunidades.items;
  const total = data.oportunidades.total;

  return (
    <SectionCard
      title={t("dashboard.section_opp_title")}
      description={t("dashboard.section_opp_desc")}
    >
      <div className="space-y-3">
        {items.map((it) => {
          const tone = OPP_TONE[it.key];
          const s = ACCENT_STYLES[tone];
          return (
            <div
              key={it.key}
              className="flex items-center gap-4 rounded-xl border border-border p-4 transition-shadow hover:shadow-sm"
            >
              <CardIcon name={OPP_ICON[it.key]} tone={tone} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-foreground">
                  {t(OPP_LABEL_KEY[it.key])}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t("dashboard.opp_subtitle_clients", { count: it.count })}
                </div>
              </div>
              <strong
                className={cx(
                  "text-base font-bold tabular-nums",
                  s.text
                )}
              >
                {formatBRL(it.valor)}
              </strong>
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex items-center justify-between rounded-xl bg-gradient-to-r from-emerald-50 to-emerald-100/50 p-4 dark:from-emerald-900/20 dark:to-emerald-900/10">
        <span className="font-semibold text-emerald-700 dark:text-emerald-400">
          {t("dashboard.opp_total")}
        </span>
        <span className="text-lg font-bold tabular-nums text-emerald-700 dark:text-emerald-400">
          {formatBRL(total)}
        </span>
      </div>
    </SectionCard>
  );
}

/* ============ 7. Saúde ============ */

const SAUDE_COLOR: Record<SaudeLabel, { hex: string; tone: AccentTone }> = {
  Ruim: { hex: "#ef4444", tone: "red" },
  Regular: { hex: "#f59e0b", tone: "yellow" },
  Boa: { hex: "#2196F3", tone: "blue" },
  Excelente: { hex: "#43A047", tone: "green" },
  "—": { hex: "#9ca3af", tone: "blue" },
};

function SaudeSection({
  data,
  t,
}: {
  data: DashboardOverview;
  t: TFunction;
}) {
  const { score, label, message } = data.saude;
  const palette = SAUDE_COLOR[label];
  const s = ACCENT_STYLES[palette.tone];

  // Sem base de calculo: gauge zerado, score exibido como "—".
  const radialData = [
    {
      name: "score",
      value: score === null ? 0 : Math.max(0, Math.min(100, score)),
      fill: palette.hex,
    },
  ];

  return (
    <SectionCard title={t("dashboard.section_health_title")}>
      <div className="flex flex-col items-center gap-4 py-2">
        <div className="relative h-[180px] w-[180px]">
          <ResponsiveContainer>
            <RadialBarChart
              cx="50%"
              cy="50%"
              innerRadius="78%"
              outerRadius="100%"
              barSize={14}
              data={radialData}
              startAngle={90}
              endAngle={-270}
            >
              <PolarAngleAxis
                type="number"
                domain={[0, 100]}
                angleAxisId={0}
                tick={false}
              />
              <RadialBar
                dataKey="value"
                cornerRadius={10}
                background={{ fill: "rgb(226 232 240)", fillOpacity: 0.5 }}
              />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <div className={cx("text-4xl font-extrabold tabular-nums", s.text)}>
              {score === null ? "—" : score}
            </div>
            <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              {t("dashboard.health_score_max")}
            </div>
          </div>
        </div>
        <div className="text-center">
          <strong className={cx("text-base font-semibold", s.text)}>
            {t(`dashboard.health_${label}`)}
          </strong>
          <p className="mt-2 max-w-xs text-center text-sm text-muted-foreground">
            {message}
          </p>
        </div>
      </div>
    </SectionCard>
  );
}

/* ============ 8. Movimentações (timeline) ============ */

const MOV_LABEL_KEY: Record<MovimentacaoTipo, string> = {
  recebimento: "dashboard.mov_type_recebimento",
  acordo: "dashboard.mov_type_acordo",
  emprestimo_criado: "dashboard.mov_type_emprestimo_criado",
};

const MOV_DESC_KEY: Record<MovimentacaoTipo, string> = {
  recebimento: "dashboard.mov_desc_recebimento",
  acordo: "dashboard.mov_desc_acordo",
  emprestimo_criado: "dashboard.mov_desc_emprestimo_criado",
};

const MOV_TONE: Record<MovimentacaoTipo, AccentTone> = {
  recebimento: "green",
  acordo: "orange",
  emprestimo_criado: "blue",
};

const MOV_ICON: Record<MovimentacaoTipo, IconName> = {
  recebimento: "check-circle",
  acordo: "handshake",
  emprestimo_criado: "plus-circle",
};

function MovimentacoesSection({
  data,
  t,
}: {
  data: DashboardOverview;
  t: TFunction;
}) {
  const movs: Movimentacao[] = data.movimentacoes;

  return (
    <SectionCard title={t("dashboard.section_movs_title")}>
      {movs.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border py-10 text-center text-sm text-muted-foreground">
          {t("dashboard.movs_empty")}
        </p>
      ) : (
        <ol className="relative space-y-4 pl-6">
          {/* thread */}
          <span
            className="absolute left-[15px] top-2 bottom-2 w-px bg-border"
            aria-hidden
          />
          {movs.map((m) => {
            const tone = MOV_TONE[m.tipo];
            const s = ACCENT_STYLES[tone];
            return (
              <li
                key={m.id}
                className="relative flex items-start gap-3"
              >
                <span
                  className={cx(
                    "absolute -left-6 flex h-8 w-8 items-center justify-center rounded-full ring-4 ring-card",
                    s.iconBg
                  )}
                >
                  <Icon
                    name={MOV_ICON[m.tipo]}
                    className={cx("h-4 w-4", s.iconFg)}
                  />
                </span>
                <div className="ml-4 flex flex-1 flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-card px-4 py-3 hover:bg-muted/40">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className={cx("font-semibold", s.text)}>
                        {t(MOV_LABEL_KEY[m.tipo])}
                      </span>
                      <span className="text-muted-foreground">·</span>
                      <span className="truncate text-foreground">
                        {m.clienteNome}
                      </span>
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {t(MOV_DESC_KEY[m.tipo])}
                    </div>
                  </div>
                  <div className="flex flex-col items-end">
                    <div className="text-sm font-bold tabular-nums text-foreground">
                      {formatBRL(m.valor)}
                    </div>
                    <div
                      className="text-[11px] text-muted-foreground"
                      title={formatTime(m.timestamp)}
                    >
                      {relativeTime(m.timestamp)}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </SectionCard>
  );
}

/* ============ 9. Mapa de clientes (drill-down) ============ */

type MapView =
  | { level: "brasil" }
  | { level: "estado"; uf: string }
  | { level: "cidade"; uf: string; cidade: string };

function MapaClientesSection({
  data,
  t,
}: {
  data: DashboardOverview;
  t: TFunction;
}) {
  const [view, setView] = useState<MapView>({ level: "brasil" });
  const porUf = data.geo.porUf;
  const totalBrasil = data.geo.totalBrasil;

  const estadoNome = (uf: string): string =>
    BR_ESTADOS_PATHS.find((p) => p.uf === uf)?.nome ?? uf;

  return (
    <SectionCard
      title={t("dashboard.section_map_title")}
      description={t("dashboard.section_map_desc")}
    >
      {/* Breadcrumb */}
      <div className="mb-4 flex items-center gap-2 text-sm">
        <button
          onClick={() => setView({ level: "brasil" })}
          className={cx(
            "rounded-md px-2 py-1 hover:bg-muted",
            view.level === "brasil"
              ? "font-semibold text-foreground"
              : "text-orange-600 dark:text-orange-400"
          )}
        >
          {t("dashboard.map_brasil")}
        </button>
        {view.level !== "brasil" && (
          <>
            <span className="text-muted-foreground">›</span>
            <button
              onClick={() => setView({ level: "estado", uf: view.uf })}
              className={cx(
                "rounded-md px-2 py-1 hover:bg-muted",
                view.level === "estado"
                  ? "font-semibold text-foreground"
                  : "text-orange-600 dark:text-orange-400"
              )}
            >
              {estadoNome(view.uf)} ({view.uf})
            </button>
          </>
        )}
        {view.level === "cidade" && (
          <>
            <span className="text-muted-foreground">›</span>
            <span className="rounded-md px-2 py-1 font-semibold text-foreground">
              {view.cidade}
            </span>
          </>
        )}
      </div>

      {view.level === "brasil" && (
        <MapaBrasil
          porUf={porUf}
          total={totalBrasil}
          t={t}
          onSelect={(uf) => setView({ level: "estado", uf })}
        />
      )}

      {view.level === "estado" && (
        <ListaCidades
          uf={view.uf}
          estadoNome={estadoNome(view.uf)}
          t={t}
          onSelect={(cidade) =>
            setView({ level: "cidade", uf: view.uf, cidade })
          }
          onBack={() => setView({ level: "brasil" })}
        />
      )}

      {view.level === "cidade" && (
        <ListaBairros
          uf={view.uf}
          cidade={view.cidade}
          t={t}
          onBack={() => setView({ level: "estado", uf: view.uf })}
        />
      )}
    </SectionCard>
  );
}

/* ---------- Nivel 1: mapa do Brasil ---------- */

function MapaBrasil({
  porUf,
  total,
  t,
  onSelect,
}: {
  porUf: Record<string, number>;
  total: number;
  t: TFunction;
  onSelect: (uf: string) => void;
}) {
  const values = Object.values(porUf);
  const maxCount = Math.max(1, ...values);
  const [hoverUf, setHoverUf] = useState<string | null>(null);

  /** cinza → orange-100 → orange-300 → orange-500 → orange-700 */
  const heatColor = (count: number) => {
    if (count === 0) return "#e5e7eb";
    const pct = count / maxCount;
    if (pct < 0.15) return "#ffedd5"; // orange-100
    if (pct < 0.35) return "#fdba74"; // orange-300
    if (pct < 0.6) return "#f97316"; // orange-500
    return "#c2410c"; // orange-700
  };

  const hoverInfo = hoverUf
    ? BR_ESTADOS_PATHS.find((p) => p.uf === hoverUf)
    : null;
  const hoverCount = hoverUf ? porUf[hoverUf] ?? 0 : 0;

  const topEstados = BR_ESTADOS_PATHS
    .map((p) => ({ ...p, count: porUf[p.uf] ?? 0 }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);

  const topMax = Math.max(1, ...topEstados.map((e) => e.count));

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[2fr_1fr]">
      <div className="relative overflow-hidden rounded-xl border border-border bg-muted/20 p-4">
        <svg
          viewBox={BR_VIEWBOX}
          preserveAspectRatio="xMidYMid meet"
          className="mx-auto h-[500px] w-full"
        >
          {BR_ESTADOS_PATHS.map((p) => {
            const count = porUf[p.uf] ?? 0;
            const isHover = hoverUf === p.uf;
            const clickable = count > 0;
            return (
              <path
                key={p.uf}
                d={p.d}
                fill={heatColor(count)}
                stroke={isHover ? "#0f172a" : "#fff"}
                strokeWidth={isHover ? 1.5 : 0.8}
                style={{
                  cursor: clickable ? "pointer" : "not-allowed",
                  transition: "fill 120ms, stroke-width 120ms",
                  filter: isHover ? "brightness(1.06)" : undefined,
                }}
                onMouseEnter={() => setHoverUf(p.uf)}
                onMouseLeave={() =>
                  setHoverUf((cur) => (cur === p.uf ? null : cur))
                }
                onClick={() => clickable && onSelect(p.uf)}
              >
                <title>
                  {p.nome} — {t("dashboard.map_client", { count })}
                </title>
              </path>
            );
          })}
        </svg>

        {hoverInfo && (
          <div className="pointer-events-none absolute left-4 top-4 rounded-lg border border-border bg-card/95 px-3 py-2 text-xs shadow-md backdrop-blur">
            <div className="font-semibold text-foreground">
              {hoverInfo.nome}
            </div>
            <div className="tabular-nums text-muted-foreground">
              {t("dashboard.map_client", { count: hoverCount })}
            </div>
          </div>
        )}

        <div className="absolute bottom-3 right-3 rounded-lg border border-border bg-card/95 px-3 py-2 text-xs shadow-sm">
          <div className="mb-1 font-semibold tabular-nums text-foreground">
            {t("dashboard.map_total_brasil", { count: total })}
          </div>
          <div className="flex items-center gap-1 text-muted-foreground">
            <span
              className="h-3 w-3 rounded-sm"
              style={{ background: "#ffedd5" }}
            />
            <span
              className="h-3 w-3 rounded-sm"
              style={{ background: "#fdba74" }}
            />
            <span
              className="h-3 w-3 rounded-sm"
              style={{ background: "#f97316" }}
            />
            <span
              className="h-3 w-3 rounded-sm"
              style={{ background: "#c2410c" }}
            />
            <span className="ml-1">{t("dashboard.map_legend_less_more")}</span>
          </div>
        </div>
      </div>

      <div>
        <div className="mb-3 text-sm font-semibold text-foreground">
          {t("dashboard.map_top_estados")}
        </div>
        <div className="rounded-xl border border-border">
          {topEstados.map((e, idx, arr) => {
            const pct = e.count === 0 ? 0 : Math.max(4, (e.count / topMax) * 100);
            return (
              <button
                key={e.uf}
                onClick={() => e.count > 0 && onSelect(e.uf)}
                disabled={e.count === 0}
                className={cx(
                  "flex w-full items-center gap-3 p-3 text-left hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60",
                  idx < arr.length - 1 ? "border-b border-border" : ""
                )}
              >
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex items-baseline justify-between gap-2">
                    <span className="truncate text-sm font-medium text-foreground">
                      {e.nome}
                    </span>
                    <span className="text-xs text-muted-foreground">{e.uf}</span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-orange-300 to-orange-600"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
                <div className="min-w-[40px] text-right text-sm font-semibold tabular-nums text-orange-600 dark:text-orange-400">
                  {e.count}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ---------- Hook: lazy GET /geo/cidades ---------- */

function useGeoCidades(uf: string | null) {
  const [data, setData] = useState<GeoNamedCount[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!uf) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getGeoCidades(uf)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uf]);

  return { data, loading, error };
}

function useGeoBairros(uf: string | null, cidade: string | null) {
  const [data, setData] = useState<GeoNamedCount[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!uf || !cidade) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getGeoBairros(uf, cidade)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uf, cidade]);

  return { data, loading, error };
}

/* ---------- Nivel 2: cidades do estado ---------- */

function ListaCidades({
  uf,
  estadoNome,
  t,
  onSelect,
  onBack,
}: {
  uf: string;
  estadoNome: string;
  t: TFunction;
  onSelect: (cidade: string) => void;
  onBack: () => void;
}) {
  const { data: cidades, loading, error } = useGeoCidades(uf);
  const total = (cidades ?? []).reduce((s, c) => s + c.count, 0);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <button
          onClick={onBack}
          className="rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted"
        >
          {t("dashboard.map_back_brasil")}
        </button>
        <div className="text-sm text-muted-foreground">
          {t("dashboard.map_total_estado", { count: total, estado: estadoNome })}
        </div>
      </div>

      {loading && (
        <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          {t("dashboard.map_loading")}
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center text-sm text-red-600 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-400">
          {t("dashboard.map_error")}
        </div>
      )}

      {!loading && !error && (cidades?.length ?? 0) === 0 && (
        <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          {t("dashboard.map_no_cidades", { estado: estadoNome })}
        </div>
      )}

      {!loading && !error && (cidades?.length ?? 0) > 0 && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {cidades!.map((c) => {
            const pct = total > 0 ? Math.round((c.count / total) * 100) : 0;
            return (
              <button
                key={c.nome}
                onClick={() => onSelect(c.nome)}
                className="rounded-xl border border-border p-4 text-left transition hover:border-orange-400 hover:shadow-sm"
              >
                <div className="flex items-baseline justify-between">
                  <span className="font-semibold text-foreground">
                    {c.nome}
                  </span>
                  <span className="text-lg font-extrabold tabular-nums text-orange-600 dark:text-orange-400">
                    {c.count}
                  </span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-orange-300 to-orange-600"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {t("dashboard.map_pct_estado", { pct })}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------- Nivel 3: bairros da cidade ---------- */

function ListaBairros({
  uf,
  cidade,
  t,
  onBack,
}: {
  uf: string;
  cidade: string;
  t: TFunction;
  onBack: () => void;
}) {
  const { data: bairros, loading, error } = useGeoBairros(uf, cidade);
  const total = (bairros ?? []).reduce((s, b) => s + b.count, 0);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <button
          onClick={onBack}
          className="rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted"
        >
          {t("dashboard.map_back_cidades", { uf })}
        </button>
        <div className="text-sm text-muted-foreground">
          {t("dashboard.map_total_cidade", { count: total, cidade })}
        </div>
      </div>

      {loading && (
        <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          {t("dashboard.map_loading")}
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center text-sm text-red-600 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-400">
          {t("dashboard.map_error")}
        </div>
      )}

      {!loading && !error && (bairros?.length ?? 0) === 0 && (
        <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          {t("dashboard.map_no_bairros", { cidade })}
        </div>
      )}

      {!loading && !error && (bairros?.length ?? 0) > 0 && (
        <div className="rounded-xl border border-border">
          {bairros!.map((b, idx) => {
            const pct = total > 0 ? Math.round((b.count / total) * 100) : 0;
            return (
              <div
                key={b.nome}
                className={cx(
                  "flex items-center gap-4 p-4",
                  idx < bairros!.length - 1 ? "border-b border-border" : ""
                )}
              >
                <div className="flex-1">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="font-semibold text-foreground">
                      {b.nome}
                    </span>
                    <span className="text-sm tabular-nums text-muted-foreground">
                      {pct}%
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-orange-300 to-orange-600"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
                <div className="min-w-[60px] text-right">
                  <div className="text-lg font-extrabold tabular-nums text-orange-600 dark:text-orange-400">
                    {b.count}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
